/* eslint formatjs/no-literal-string-in-jsx: "error" */
import React, { useState } from "react"
import { v4 as uuid } from "uuid"
import { Container, FlexContainer, H2, Stack } from "@carrotfertility/carotene"
import { UploadFile } from "components/views/reimbursements/upload/UploadFile"
import ArrowSubmitButton from "features/legacy-components/ArrowSubmitButton"
import { UploadDropzoneWithIllustration } from "components/views/reimbursements/upload/UploadDropzoneWithIllustration"
import { useStateMachine } from "components/context/stateMachine/StateMachineV2"
import CarrotClient from "lib/carrot-api/index"
import AttachmentStates from "../../../utils/AttachmentStates"
import Settings from "../../../utils/CarrotConfig"
import { FileTypes, useReimbursementContext } from "components/context/reimbursement/GetReimbursedContext"
import { BackArrow } from "../../views/fle/Molecules"
import FileCategories from "../../../utils/FileCategories"
import { HR } from "components/views/atoms/Atoms"
import { FormattedMessage } from "react-intl"

const client = new CarrotClient(Settings.CARROT_BACKEND_URL)
const MAX_FILE_SIZE = 30 * 1000000 // 30 MB

export interface AttachmentType {
  file: File
  state: AttachmentStates
  guid: string
  fileCategory: string
}

const uploadAttachment = async (attachment: AttachmentType, guid: string, fileCategory: string): Promise<string> => {
  const successResponseGuid = await client.uploadReimbursementFile(attachment.file, guid, fileCategory)
  return successResponseGuid
}

const findAttachmentByGuid = (attachments: AttachmentType[], guid: string): AttachmentType =>
  attachments.find((attachment) => attachment.guid === guid)

const toUploading = (attachment: AttachmentType): AttachmentType => {
  attachment.state = AttachmentStates.UPLOADING
  return attachment
}
const toComplete = (attachment: AttachmentType): AttachmentType => {
  attachment.state = AttachmentStates.COMPLETED
  return attachment
}
const toFailed = (attachment: AttachmentType): AttachmentType => {
  attachment.state = AttachmentStates.FAILED
  return attachment
}

const toVirusDetected = (attachment: AttachmentType): AttachmentType => {
  attachment.state = AttachmentStates.VIRUSDETECTED
  return attachment
}

const getFileNameAndGuid = (attachment: AttachmentType): Record<string, string> => ({
  guid: attachment.guid,
  name: attachment.file.name
})

export const AboutThisExpenseUploadFile = ({ fileCategory, progress, uploadHeader, body }): JSX.Element => {
  const { updateStatementFiles, updateDoulaFiles, state, expenseId } = useReimbursementContext()
  const isCardExpense = !!expenseId

  const [attachments, setAttachments] = useState<AttachmentType[]>(() => {
    // if the user has come back to this step we initialize the list of attachments
    // from the file name and guid saved in the reimbursement context. we can assume the state is COMPLETED
    const stateFiles = fileCategory === FileCategories.STATEMENT ? state.statementFiles : state.doulaFiles
    if (stateFiles) {
      return stateFiles.map((file: any) => {
        return {
          file: {
            name: file.name
          },
          state: AttachmentStates.COMPLETED,
          guid: file.guid,
          fileCategory: fileCategory
        } as AttachmentType
      })
    }
    return []
  })

  const { send, back } = useStateMachine()

  const processAcceptedDrop = (files): void => {
    const newAttachments: AttachmentType[] = files.map((file) => ({
      file,
      state: file.size > MAX_FILE_SIZE ? AttachmentStates.OVERSIZED : AttachmentStates.UPLOADING,
      guid: uuid(),
      fileCategory: fileCategory
    }))

    setAttachments((prevAttachments) => [...prevAttachments, ...newAttachments])

    const attachmentsToUpload = newAttachments.filter((a) => a.file.size <= MAX_FILE_SIZE)

    attachmentsToUpload.forEach(async (attachment) => {
      const guid = attachment.guid
      try {
        await uploadAttachment(attachment, guid, fileCategory)
        toComplete(attachment)
      } catch (error) {
        const errorBody = await error?.response?.text()
        if (errorBody == "Virus detected in file upload") {
          toVirusDetected(attachment)
        } else {
          toFailed(attachment)
        }
      } finally {
        setAttachments((attachments) => {
          const index = attachments.findIndex((a) => a.guid === attachment.guid)
          attachments[index] = attachment
          return [...attachments]
        })
      }
    })
  }

  const processRejectedDrop = (files): void => {
    const newAttachments: AttachmentType[] = files.map((file) => ({
      file: file.file,
      state: AttachmentStates.INVALIDTYPE,
      guid: uuid(),
      fileCategory: fileCategory
    }))

    setAttachments((prevAttachments) => [...prevAttachments, ...newAttachments])
  }

  // removes the attachment from component state
  const onDelete = (guid: string): void => {
    setAttachments((prevAttachments) => prevAttachments.filter((a) => a.guid !== guid))
  }
  const onRetry = async (guid: string): Promise<void> => {
    const attachment = toUploading(findAttachmentByGuid(attachments, guid))
    try {
      await uploadAttachment(attachment, guid, fileCategory)
      toComplete(attachment)
    } catch {
      toFailed(attachment)
    } finally {
      setAttachments((attachments) => {
        const index = attachments.findIndex((a) => a.guid === attachment.guid)
        attachments[index] = attachment
        return [...attachments]
      })
    }
  }

  // when the user goes to the next step we update the reimbursement context with the attachment guids
  const handleContinueClick = (): void => {
    if (fileCategory === FileCategories.STATEMENT) {
      updateStatementFiles(attachments.map(getFileNameAndGuid) as FileTypes)
      send(!isCardExpense ? "ABOUT_EXPENSE_ELIGIBLE_REIMBURSEMENT_AMOUNT" : "")
    } else if (fileCategory === FileCategories.DOULA) {
      updateDoulaFiles(attachments.map(getFileNameAndGuid) as FileTypes)
      send("")
    }
  }

  const showBackArrow = (): JSX.Element => {
    if (fileCategory != FileCategories.STATEMENT) {
      return <BackArrow onClick={back} />
    }
    return null
  }

  return (
    <>
      {progress}
      {showBackArrow()}
      <Container paddingTopBottom="medium">
        <H2>{uploadHeader}</H2>
        {body}
      </Container>
      <Container
        sx={{
          paddingBottom: "huge"
        }}
      >
        <UploadDropzoneWithIllustration
          showNew
          small
          onDropAccepted={processAcceptedDrop}
          onDropRejected={processRejectedDrop}
        />
      </Container>
      {attachments.map((attachment) => (
        <Stack key={attachment.guid} spacing="small" stack="small">
          <HR color="black-10" />
          <UploadFile attachment={attachment} onDelete={onDelete} onRetry={onRetry} />
        </Stack>
      ))}
      <FlexContainer paddingTopBottom="huge" justifyContent="flex-end">
        <ArrowSubmitButton
          enabled={
            attachments.length > 0 && attachments.filter((a) => a.state != AttachmentStates.COMPLETED).length == 0
          }
          onClick={handleContinueClick}
        >
          <FormattedMessage defaultMessage="Continue" />
        </ArrowSubmitButton>
      </FlexContainer>
    </>
  )
}
